import React from 'react';
import { ChevronRight } from 'lucide-react';
import MaskedMobile from '../../common/MaskedMobile';
import Pagination from '../../common/Pagination';
import { MarketingOrdersTable } from './MarketingOrdersTable';
import {
  ExecutiveIncentiveRow, ExecMonthSlab, execKey, execSlabText, isBelowFirstSlab, monthLabel, monthDates,
} from './marketingOrdersView';
import { LoadRowOrders, ORDERS_PAGE_SIZE, useExpandedOrders } from './useExpandedOrders';
import ExecIncentiveStatusCell from './ExecIncentiveStatusCell';

const formatINR = (val?: number) => '₹' + Number(val || 0).toLocaleString('en-IN');

interface Props {
  rows: ExecutiveIncentiveRow[];
  canApprove: (row: ExecutiveIncentiveRow) => boolean;
  /** execKey of the row whose bulk approval is in flight. */
  approvingKey: string | null;
  onApprove: (row: ExecutiveIncentiveRow) => void;
  /**
   * Fetches one page of a row's orders when it is expanded. Its identity
   * changes with the page filters, which re-fetches the open row.
   */
  loadOrders: LoadRowOrders<ExecutiveIncentiveRow>;
  isOrgWide?: boolean;
  isMarketingManager?: boolean;
  marketingStaff?: any[];
  farmStaff?: any[];
  adminMobile?: string;
  hasApproveAccess?: boolean;
}

const slabCell = (s?: ExecMonthSlab) => {
  const t = execSlabText(s);
  if (!t) return <span className="text-slate-300">—</span>;
  const below = isBelowFirstSlab(s?.slab);
  return (
    <div className="flex flex-col items-center gap-0.5 whitespace-nowrap">
      <span className={`inline-flex px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wide ${
        below ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`}>
        {t.slab}
      </span>
    </div>
  );
};

const HEADERS = ['S.No', 'Executive', 'Month', 'Orders', 'Total Units', 'Total Incentives', 'Direct Units', 'Direct Incentives', 'Direct Slab', 'Indirect Units', 'Indirect Incentives', 'Indirect Slab', 'Status', 'Action'];
const CENTER_COLS = ['S.No', 'Orders', 'Total Units', 'Total Incentives', 'Direct Units', 'Direct Incentives', 'Direct Slab', 'Indirect Units', 'Indirect Incentives', 'Indirect Slab'];

/**
 * Executive-wise orders and incentive slabs for the My Orders screen — one
 * row per executive per incentive month (the order approval month), over the
 * same filters as the header tiles. The slab is where the month lands once
 * its pending orders are approved.
 */
const ExecutiveIncentivesCard: React.FC<Props> = ({
  rows, canApprove, approvingKey, onApprove, loadOrders,
  isOrgWide, isMarketingManager, marketingStaff, farmStaff, adminMobile,
  hasApproveAccess = false
}) => {
  // Like a paid order's cycle view: one row open at a time, its orders
  // fetched on open and shown in a row directly beneath it.
  const {
    expandedKey, toggle, page: ordersPage, setPage: setOrdersPage,
    orders, totalPages, loading: loadingOrders, error: ordersError,
  } = useExpandedOrders(rows, execKey, loadOrders);

  if (!rows.length) return null;
  return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="text-sm font-bold text-slate-800">Executive-wise Orders &amp; Incentive Slabs</div>
        </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b-2 border-slate-100">
              {HEADERS.filter(h => h !== 'Action' || hasApproveAccess).map((h) => (
                <th key={h} className={`px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap ${CENTER_COLS.includes(h) ? 'text-center' : 'text-left'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r, index) => {
              const key = execKey(r);
              const selected = expandedKey === key;
              return (
                <React.Fragment key={key}>
                  <tr className={`transition-colors ${selected ? 'bg-emerald-50/50' : 'hover:bg-slate-50/70'}`}>
                    <td className="px-4 py-3 font-semibold text-slate-500 text-[13px] text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => toggle(key)}
                          className="p-0.5 hover:bg-slate-200 rounded transition-colors border-none bg-transparent cursor-pointer shrink-0"
                        >
                          <ChevronRight
                            size={14}
                            className={`text-slate-400 transition-transform duration-200 ${selected ? 'rotate-90' : ''}`}
                          />
                        </button>
                        <span>{index + 1}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5 whitespace-nowrap">
                        <span className="font-bold text-slate-800 text-[13px]">{r.exec_name || r.exec_mobile}</span>
                        <MaskedMobile mobile={r.exec_mobile} className="text-slate-500 font-medium text-[12px]" />
                        {r.exec_role && <span className="text-[10px] text-slate-400 uppercase tracking-wide">{r.exec_role}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-slate-700 font-semibold text-[13px]">{monthLabel(r.month)}</div>
                      <div className="text-[11px] text-slate-500">{monthDates(r.month)}</div>
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <div className="flex flex-col items-center gap-1.5">
                        <span className="font-bold text-[13px] text-slate-800">{r.paid_orders}</span>
                        <button
                          type="button"
                          onClick={() => toggle(key)}
                          className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wide rounded-lg transition-colors ${
                            selected
                              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {selected ? 'Close' : 'View'}
                        </button>
                      </div>
                    </td>
                  <td className="px-4 py-3 text-slate-800 font-bold text-[13px] whitespace-nowrap text-center">
                    {r.paid_units}
                  </td>
                  <td className="px-4 py-3 font-black text-slate-900 text-[13px] whitespace-nowrap text-center">
                    {formatINR(r.total_incentive)}
                  </td>
                  <td className="px-4 py-3 text-slate-700 font-bold text-[13px] whitespace-nowrap text-center">
                    {r.direct_paid_units}
                  </td>
                  <td className="px-4 py-3 text-emerald-700 font-bold text-[13px] whitespace-nowrap text-center">
                    {formatINR(r.credited_direct + r.pending_direct)}
                  </td>
                  <td className="px-4 py-3 text-center whitespace-nowrap">
                    {slabCell(r.direct_slab)}
                  </td>
                  <td className="px-4 py-3 text-slate-700 font-bold text-[13px] whitespace-nowrap text-center">
                    {r.indirect_paid_units}
                  </td>
                  <td className="px-4 py-3 text-emerald-700 font-bold text-[13px] whitespace-nowrap text-center">
                    {formatINR(r.credited_indirect + r.pending_indirect)}
                  </td>
                  <td className="px-4 py-3 text-center whitespace-nowrap">
                    {slabCell(r.indirect_slab)}
                  </td>
                  <td className="px-4 py-3">
                    {/* Pending orders are PAID and unapproved, so the rest of
                        the paid orders are the approved (credited) ones. */}
                    <ExecIncentiveStatusCell
                      pendingOrders={r.pending_orders}
                      pendingAmount={r.pending_amount}
                      creditedOrders={Math.max(0, r.paid_orders - r.pending_orders)}
                      creditedAmount={r.credited_amount}
                    />
                  </td>
                  {hasApproveAccess && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {canApprove(r) && (
                          <button
                            type="button"
                            onClick={() => onApprove(r)}
                            disabled={approvingKey === key}
                            className="px-3 py-1.5 rounded-xl bg-[#1B4332] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#143427] transition-all shadow-xs disabled:opacity-50 cursor-pointer"
                          >
                            {approvingKey === key ? 'Approving…' : 'Approve all'}
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
                {selected && (
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <td colSpan={hasApproveAccess ? HEADERS.length : HEADERS.length - 1} className="px-6 py-5">
                      <div className="space-y-3">
                        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                          Orders — {r.exec_name || r.exec_mobile} · {monthLabel(r.month)}
                        </p>
                        {ordersError ? (
                          <p className="text-[12px] text-rose-600">{ordersError}</p>
                        ) : (
                        <MarketingOrdersTable
                          orders={orders}
                          loading={loadingOrders}
                          page={ordersPage}
                          pageSize={ORDERS_PAGE_SIZE}
                          isOrgWide={!!isOrgWide}
                          isMarketingManager={!!isMarketingManager}
                          marketingStaff={marketingStaff || []}
                          farmStaff={farmStaff || []}
                          adminMobile={adminMobile}
                        />
                        )}
                        <Pagination currentPage={ordersPage} totalPages={totalPages} onPageChange={setOrdersPage} />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExecutiveIncentivesCard;
